import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

const PRODUCT_NAME = 'ARC Constellation';

const ENGINE_COLORS = [
  { core: '225,65,60', halo: '255,115,105' },
  { core: '90,150,235', halo: '140,190,255' },
  { core: '180,100,225', halo: '215,150,245' },
  { core: '120,210,120', halo: '170,235,165' },
  { core: '70,210,220', halo: '130,235,240' },
  { core: '245,150,70', halo: '255,185,115' },
];

const ENGINES = [
  { name: 'Titan', kind: 'titan', trait: 'Force, momentum, will', colorIdx: 0 },
  { name: 'Arbiter', kind: 'arbiter', trait: 'Judgment, clarity, structure', colorIdx: 1 },
  { name: 'Alchemist', kind: 'alchemist', trait: 'Transformation, invention', colorIdx: 2 },
  { name: 'Artisan', kind: 'artisan', trait: 'Craft, taste, expression', colorIdx: 3 },
  { name: 'Oracle', kind: 'oracle', trait: 'Insight, foresight, pattern', colorIdx: 4 },
  { name: 'Sovereign', kind: 'sovereign', trait: 'Vision, sovereignty, command', colorIdx: 5 },
];

const ACCELERATOR_SURFACES = [
  {
    label: 'Find ventures',
    index: '01',
    title: 'Early-stage founders with real signal.',
    body: 'Discover ventures through what the founder is building, what they need next, and how they make decisions under pressure.',
    note: 'Founder first',
  },
  {
    label: 'Find talent',
    index: '02',
    title: 'Skilled builders for the gaps that matter.',
    body: 'Bring in technical, product, commercial, creative, and execution talent with context beyond a CV or cold intro.',
    note: 'Gap focused',
  },
  {
    label: 'Find angels',
    index: '03',
    title: 'Capital conversations with stronger context.',
    body: 'Connect founders and approved angels around venture stage, sector, current needs, and founder Arc signal.',
    note: 'Structured access',
  },
];

const PROCESS_STEPS = [
  {
    number: '01',
    title: 'Waitlist',
    body: 'Join the Cohort 1 review list. Every signup starts with a real email and a saved account.',
  },
  {
    number: '02',
    title: 'Brain Scan',
    body: 'Complete the 60-question diagnostic. It creates your Arc profile and founder signal.',
  },
  {
    number: '03',
    title: 'Application',
    body: 'Share what you are building, what stage you are at, and what help would move the venture forward.',
  },
  {
    number: '04',
    title: 'Founder call',
    body: 'Strong applications move into a short call and the next step for Cohort 1 review.',
  },
];

const FAQ = [
  {
    q: `What is ${PRODUCT_NAME}?`,
    a: 'ARC Constellation is a curated startup accelerator for early-stage founders. It starts with the Brain Scan, creates an Arc profile, and uses that signal to review founders with more context than a pitch deck alone.',
  },
  {
    q: 'What is the Brain Scan?',
    a: 'The Brain Scan is a 60-question diagnostic that maps how you build, decide, lead, and respond under pressure. It produces your Arc profile and becomes part of your Cohort 1 application signal.',
  },
  {
    q: 'Why join the waitlist?',
    a: 'The waitlist puts you on the Cohort 1 review list and saves your Arc profile to your email. As the accelerator opens, it gives ARC Constellation the context needed to invite the right founders into the next step.',
  },
  {
    q: 'Who can join?',
    a: 'The first public focus is founders building early-stage ventures. Skilled talent and angels are part of the wider constellation and will become more visible as the platform opens.',
  },
  {
    q: 'What stage is this at?',
    a: 'ARC Constellation is pre-launch and accepting Cohort 1 interest. The current flow is landing page, Brain Scan, saved Arc profile, and waitlist review.',
  },
];

function ArrowIcon({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 14 14" fill="none" aria-hidden="true">
      <path d="M2 7h10M8 3l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function EngineIcon({ kind }) {
  const common = {
    width: 40,
    height: 40,
    viewBox: '0 0 40 40',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.5,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    'aria-hidden': true,
  };

  switch (kind) {
    case 'titan':
      return (
        <svg {...common}>
          <path d="M20 4 L31 33 L20 27 L9 33 Z" />
          <path d="M20 4 L20 27" strokeOpacity="0.4" />
        </svg>
      );
    case 'arbiter':
      return (
        <svg {...common}>
          <path d="M20 5 L33 12.5 L33 27.5 L20 35 L7 27.5 L7 12.5 Z" />
          <path d="M7 20 L33 20" strokeOpacity="0.45" />
          <path d="M20 5 L20 35" strokeOpacity="0.45" />
        </svg>
      );
    case 'alchemist':
      return (
        <svg {...common}>
          <circle cx="20" cy="22" r="13" />
          <path d="M20 9 L31 28 L9 28 Z" strokeOpacity="0.85" />
        </svg>
      );
    case 'artisan':
      return (
        <svg {...common}>
          <path d="M8 30 C 12 14, 28 14, 32 30" />
          <path d="M8 30 C 14 22, 26 22, 32 30" strokeOpacity="0.55" />
          <circle cx="20" cy="10" r="1.6" fill="currentColor" />
        </svg>
      );
    case 'oracle':
      return (
        <svg {...common}>
          <circle cx="20" cy="20" r="14" strokeOpacity="0.35" />
          <circle cx="20" cy="20" r="9" strokeOpacity="0.7" />
          <circle cx="20" cy="20" r="3.2" fill="currentColor" />
        </svg>
      );
    case 'sovereign':
      return (
        <svg {...common}>
          <path d="M20 4 L22 18 L36 20 L22 22 L20 36 L18 22 L4 20 L18 18 Z" />
          <circle cx="20" cy="20" r="2" fill="currentColor" />
        </svg>
      );
    default:
      return null;
  }
}

function useRevealOnScroll() {
  useEffect(() => {
    const elements = document.querySelectorAll('.ev-reveal');
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-in');
        observer.unobserve(entry.target);
      });
    }, { threshold: 0.18 });

    elements.forEach((element) => observer.observe(element));
    return () => observer.disconnect();
  }, []);
}

function ConstellationCanvas() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const ctx = canvas.getContext('2d');
    let width = 0;
    let height = 0;
    let frame = 0;
    const mouse = { x: -1000, y: -1000 };

    const seed = (i, k) => {
      const value = Math.sin(i * 12.9898 + k * 78.233) * 43758.5453;
      return value - Math.floor(value);
    };

    const nodes = Array.from({ length: 320 }, (_, i) => {
      const revealRoll = seed(i, 11);
      const revealAt = revealRoll < 0.55 ? -0.1 : (revealRoll - 0.55) / 0.45;
      const loner = revealAt > 0.4 ? seed(i, 12) < 0.6 : seed(i, 12) < 0.1;
      const pulseRoll = seed(i, 7);

      return {
        x: seed(i, 1),
        y: seed(i, 2),
        r: 0.55 + seed(i, 3) * 1.6,
        phase: seed(i, 4) * Math.PI * 2,
        bright: 0.4 + seed(i, 5) * 0.55,
        member: seed(i, 6) < 0.12 && !loner,
        pulse: pulseRoll < 0.14 ? 0.85 + seed(i, 8) * 0.25 : pulseRoll < 0.38 ? 0.3 + seed(i, 8) * 0.25 : 0,
        pulseSpeed: 0.7 + seed(i, 9) * 1.1,
        alive: seed(i, 10) < 0.08 && !loner,
        revealAt,
        loner,
        hub: !loner && seed(i, 16) < 0.06,
        engine: Math.floor(seed(i, 15) * 6) % 6,
      };
    });

    const resize = () => {
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const scrollProgress = () => {
      const documentHeight = document.documentElement.scrollHeight - window.innerHeight;
      return documentHeight > 0 ? Math.max(0, Math.min(1, window.scrollY / documentHeight)) : 0;
    };

    const draw = (time) => {
      const progress = scrollProgress();
      const seconds = time * 0.001;
      const drift = time * 0.00012;
      const connectDist = 70 + progress * 120;
      const connectDistSq = connectDist * connectDist;
      const baseAlpha = 0.4 + progress * 0.18;
      const breathe = 0.5 + 0.5 * Math.sin(seconds * 0.9);

      ctx.clearRect(0, 0, width, height);

      const points = nodes.reduce((acc, node) => {
        const revealValue = (progress - node.revealAt) / 0.06;
        const reveal = revealValue <= 0 ? 0 : revealValue >= 1 ? 1 : revealValue;
        if (reveal <= 0.001) return acc;

        const x = node.x * width + Math.sin(drift + node.phase) * 6;
        const y = node.y * height + Math.cos(drift * 0.9 + node.phase * 0.7) * 6;
        const dx = x - mouse.x;
        const dy = y - mouse.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        const proximity = distance < 180 ? 1 - distance / 180 : 0;
        const sinNode = (Math.sin(seconds * node.pulseSpeed + node.phase * 1.7) + 1) * 0.5;
        const brightMod = node.pulse ? 1 - node.pulse * 0.3 + node.pulse * sinNode * 0.6 : 1 + 0.08 * Math.sin(seconds * 0.7 + node.phase * 3);
        const radiusMod = node.pulse ? 1 + node.pulse * (sinNode - 0.5) * 0.9 : 1;

        acc.push({
          x,
          y,
          r: node.r * radiusMod,
          bright: node.bright * brightMod * reveal,
          member: node.member,
          alive: node.alive,
          loner: node.loner,
          hub: node.hub,
          proximity,
          reveal,
          engine: node.engine,
        });
        return acc;
      }, []);

      ctx.lineWidth = 0.7;
      for (let i = 0; i < points.length; i += 1) {
        const current = points[i];
        if (current.loner) continue;

        for (let j = i + 1; j < points.length; j += 1) {
          const next = points[j];
          if (next.loner) continue;

          const dx = current.x - next.x;
          const dy = current.y - next.y;
          const distanceSq = dx * dx + dy * dy;
          if (distanceSq > connectDistSq) continue;

          const distance = Math.sqrt(distanceSq);
          const strength = 1 - distance / connectDist;
          const cursorBoost = Math.max(current.proximity, next.proximity) * 0.28;
          const revealStrength = current.reveal * next.reveal;
          let alpha = ((0.04 + progress * 0.11) * strength + cursorBoost * strength) * revealStrength;
          const alive = current.alive || next.alive;
          if (alive) alpha += strength * (0.18 + breathe * 0.35) * revealStrength;

          ctx.strokeStyle = alive
            ? `rgba(232,200,150,${Math.min(0.85, alpha)})`
            : `rgba(199,164,109,${alpha})`;
          ctx.beginPath();
          ctx.moveTo(current.x, current.y);
          ctx.lineTo(next.x, next.y);
          ctx.stroke();
        }
      }

      const hubs = points.filter((point) => point.hub && !point.loner);
      hubs.forEach((hub) => {
        points.forEach((point) => {
          if (point === hub || point.loner) return;
          const dx = hub.x - point.x;
          const dy = hub.y - point.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          if (distance < connectDist || distance > connectDist * 1.6) return;

          const strength = 1 - (distance - connectDist) / (connectDist * 0.6);
          const alpha = (0.06 + 0.05 * progress) * strength * hub.reveal * point.reveal;
          ctx.strokeStyle = `rgba(199,164,109,${alpha})`;
          ctx.lineWidth = 0.55;
          ctx.beginPath();
          ctx.moveTo(hub.x, hub.y);
          ctx.lineTo(point.x, point.y);
          ctx.stroke();
        });
      });

      points.forEach((point) => {
        const palette = ENGINE_COLORS[point.engine] || ENGINE_COLORS[5];
        const alpha = Math.min(1, baseAlpha * point.bright + point.proximity * 0.35);
        const radius = point.r * 0.9 + point.proximity * 0.8;
        const haloStrength = point.proximity * 0.55 + (point.member ? 0.16 : 0);

        if (haloStrength > 0.06) {
          const haloRadius = radius * 4.5;
          const gradient = ctx.createRadialGradient(point.x, point.y, 0, point.x, point.y, haloRadius);
          gradient.addColorStop(0, `rgba(${palette.halo},${Math.min(0.85, haloStrength * 0.55)})`);
          gradient.addColorStop(1, `rgba(${palette.core},0)`);
          ctx.fillStyle = gradient;
          ctx.beginPath();
          ctx.arc(point.x, point.y, haloRadius, 0, Math.PI * 2);
          ctx.fill();
        }

        const rgb = point.member || point.alive ? palette.halo : palette.core;
        ctx.fillStyle = `rgba(${rgb},${Math.min(1, alpha * 1.05)})`;
        ctx.beginPath();
        ctx.arc(point.x, point.y, radius, 0, Math.PI * 2);
        ctx.fill();
      });

      frame = requestAnimationFrame(draw);
    };

    const handleMouseMove = (event) => {
      mouse.x = event.clientX;
      mouse.y = event.clientY;
    };

    const handleMouseLeave = () => {
      mouse.x = -1000;
      mouse.y = -1000;
    };

    resize();
    window.addEventListener('resize', resize);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseleave', handleMouseLeave);
    frame = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, []);

  return <canvas ref={canvasRef} className="ev-canvas" aria-hidden="true" />;
}

function SurfaceCard({ item, index }) {
  const color = ENGINE_COLORS[index % ENGINE_COLORS.length];

  return (
    <div className="ev-reveal" style={{ transitionDelay: `${index * 90}ms` }}>
      <div className="ev-node-card" style={{ borderColor: `rgba(${color.core}, 0.35)`, height: '100%' }}>
        <div className="ev-card-top">
          <span className="ev-member-mark" style={{ color: `rgba(${color.halo},0.95)` }}>{item.index}</span>
          <span className="ev-id">{item.label}</span>
        </div>
        <div className="ev-small-label" style={{ color: `rgba(${color.halo},0.95)` }}>Surface</div>
        <div className="ev-member-name">{item.title}</div>
        <div className="ev-member-role">{item.body}</div>
        <div className="ev-color-line" style={{ background: `rgba(${color.core},0.25)` }} />
        <div className="ev-looking-for" style={{ background: `rgba(${color.core},0.08)`, borderColor: `rgba(${color.core},0.25)` }}>
          <div className="ev-small-label" style={{ color: `rgba(${color.halo},0.9)` }}>Focus</div>
          <div>{item.note}</div>
        </div>
      </div>
    </div>
  );
}

function EngineCard({ engine, index }) {
  const color = ENGINE_COLORS[engine.colorIdx];

  return (
    <div className="ev-reveal" style={{ transitionDelay: `${index * 70}ms` }}>
      <div className="ev-card ev-card-hover ev-engine-card" style={{ borderColor: `rgba(${color.core},0.22)` }}>
        <div
          className="ev-engine-icon"
          style={{
            background: `radial-gradient(circle at 30% 30%, rgba(${color.halo},0.22), rgba(${color.core},0.04))`,
            borderColor: `rgba(${color.core},0.35)`,
            color: `rgba(${color.halo},0.95)`,
          }}
        >
          <EngineIcon kind={engine.kind} />
        </div>
        <div>
          <div className="ev-small-label" style={{ color: `rgba(${color.halo},0.85)` }}>Engine</div>
          <div className="ev-engine-title">{engine.name}</div>
          <div className="ev-engine-trait">{engine.trait}</div>
        </div>
      </div>
    </div>
  );
}

function ProcessStep({ step, index }) {
  const color = ENGINE_COLORS[index % ENGINE_COLORS.length];

  return (
    <div className="ev-node-card ev-process-step" style={{ borderColor: `rgba(${color.core},0.35)` }}>
      <div className="ev-card-top">
        <span className="ev-member-mark" style={{ color: `rgba(${color.halo},0.95)` }}>{step.number}</span>
        <span className="ev-id">Cohort 1</span>
      </div>
      <div className="ev-member-name">{step.title}</div>
      <div className="ev-member-role">{step.body}</div>
    </div>
  );
}

function FaqItem({ item, isOpen, onClick, index }) {
  return (
    <div className="ev-reveal" style={{ transitionDelay: `${index * 60}ms` }}>
      <button className={`ev-faq-btn ${isOpen ? 'is-open' : ''}`} type="button" aria-expanded={isOpen} onClick={onClick}>
        <div className="ev-faq-head">
          <span className="ev-faq-q">{item.q}</span>
          <span className="ev-faq-plus">+</span>
        </div>
        <div className="ev-faq-a">
          <p>{item.a}</p>
        </div>
      </button>
    </div>
  );
}

export default function NewLanding() {
  const [openFaq, setOpenFaq] = useState(null);
  const [scrolled, setScrolled] = useState(false);

  useRevealOnScroll();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 80);
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="ev-page">
      <ConstellationCanvas />

      <nav className={`ev-nav ${scrolled ? 'is-scrolled' : ''}`}>
        <Link to="/" className="ev-logo">
          <img className="ev-logo-img" src="/assets/arc-constellation-logo.png" alt="ARC Constellation" />
        </Link>
        <div className="ev-nav-actions">
          <a href="#process" className="ev-cta-ghost ev-nav-v2">How it works</a>
          <Link to="/assessment" className="ev-cta ev-cta-sm">
            <span>Join the waitlist</span>
            <ArrowIcon size={12} />
          </Link>
        </div>
      </nav>

      <main className="ev-content">
        <section className="ev-section ev-hero">
          <div className="ev-reveal">
            <div className="ev-pill">
              <span className="ev-pill-dot" />
              Cohort 1. Accepting applications
            </div>
          </div>
          <div className="ev-reveal ev-delay-1">
            <h1 className="ev-display ev-hero-title">
              An accelerator<br />
              for founders who <span className="ev-gold-grad ev-italic">build first.</span>
            </h1>
          </div>
          <div className="ev-reveal ev-delay-2">
            <p className="ev-hero-copy">
              {PRODUCT_NAME} is a curated accelerator for early-stage founders.
              We assess how you build, decide, and lead before any conversation about terms.
              Cohort 1 is limited and reviewed by hand.
            </p>
          </div>
          <div className="ev-reveal ev-delay-3">
            <p className="ev-hero-subcopy">
              Start with the Brain Scan. Your Arc profile becomes the first signal in your Cohort 1 application.
            </p>
          </div>
          <div className="ev-reveal ev-delay-4">
            <div className="ev-cta-row">
              <Link to="/assessment" className="ev-cta">
                <span>Join the waitlist</span>
                <ArrowIcon />
              </Link>
              <a href="#process" className="ev-cta-ghost">How it works</a>
            </div>
          </div>
        </section>

        <section className="ev-section">
          <div className="ev-reveal">
            <div className="ev-center ev-section-head">
              <div className="ev-rule">What we do</div>
              <h2 className="ev-display ev-section-title">
                Three ways to <span className="ev-gold-grad ev-italic">compound.</span>
              </h2>
              <p>
                ARC Constellation starts with founders, then connects them to the talent and angels needed to move faster with less noise.
              </p>
            </div>
          </div>
          <div className="ev-grid-3 ev-audience-grid">
            {ACCELERATOR_SURFACES.map((item, index) => (
              <SurfaceCard key={item.label} item={item} index={index} />
            ))}
          </div>
        </section>

        <section className="ev-section">
          <div className="ev-reveal">
            <div className="ev-center ev-section-head">
              <div className="ev-rule">The system</div>
              <h2 className="ev-display ev-section-title">
                Six engines.<br />
                <span className="ev-gold-grad ev-italic">Thirty arcs.</span>
              </h2>
              <p>
                Your top two engines combine into one of thirty Arcs. Practical operating psychology, not a personality quiz.
              </p>
            </div>
          </div>
          <div className="ev-grid-3">
            {ENGINES.map((engine, index) => (
              <EngineCard key={engine.name} engine={engine} index={index} />
            ))}
          </div>
        </section>

        <section id="process" className="ev-section">
          <div className="ev-reveal">
            <div className="ev-center ev-section-head">
              <div className="ev-rule">The process</div>
              <h2 className="ev-display ev-section-title">
                Four steps to <span className="ev-gold-grad ev-italic">Cohort 1.</span>
              </h2>
              <p>
                The waitlist is not a dead form. It starts the path from founder signal to application review.
              </p>
            </div>
          </div>
          <div className="ev-reveal">
            <div className="ev-grid-2 ev-process-grid">
              {PROCESS_STEPS.map((step, index) => (
                <ProcessStep key={step.number} step={step} index={index} />
              ))}
            </div>
          </div>
        </section>

        <section id="network" className="ev-section ev-network-section">
          <div className="ev-reveal">
            <div className="ev-rule">The constellation</div>
            <h2 className="ev-display ev-network-title">
              A private startup <span className="ev-gold-grad ev-italic">constellation.</span>
            </h2>
            <p>
              Founders are the first public focus. Skilled talent and angels become part of the system as the accelerator opens.
              The goal is useful introductions, stronger venture context, and less wasted attention.
            </p>
          </div>
          <div className="ev-reveal ev-delay-2">
            <div className="ev-pill-row">
              <span className="ev-pill"><span className="ev-pill-dot" />Founders</span>
              <span className="ev-pill"><span className="ev-pill-dot" />Skilled talent</span>
              <span className="ev-pill"><span className="ev-pill-dot" />Angels</span>
              <span className="ev-pill"><span className="ev-pill-dot" />Ventures</span>
            </div>
          </div>
          <div className="ev-reveal ev-delay-3">
            <div className="ev-grid-3 ev-stat-grid">
              <div className="ev-card ev-card-hover">
                <div className="ev-stat-index">/ 01</div>
                <div className="ev-display ev-gold-grad ev-stat-value">06</div>
                <div className="ev-stat-label">Engines</div>
                <div className="ev-stat-body">Practical operating psychology, not personality.</div>
              </div>
              <div className="ev-card ev-card-hover">
                <div className="ev-stat-index">/ 02</div>
                <div className="ev-display ev-gold-grad ev-stat-value">30</div>
                <div className="ev-stat-label">Arcs</div>
                <div className="ev-stat-body">Two engines combine into one practical signal.</div>
              </div>
              <div className="ev-card ev-card-hover">
                <div className="ev-stat-index">/ 03</div>
                <div className="ev-display ev-gold-grad ev-stat-value">01</div>
                <div className="ev-stat-label">Cohort</div>
                <div className="ev-stat-body">A limited founder intake reviewed by hand.</div>
              </div>
            </div>
          </div>
        </section>

        <section className="ev-section">
          <div className="ev-reveal">
            <div className="ev-center ev-section-head">
              <div className="ev-rule">Questions</div>
              <h2 className="ev-display ev-section-title">
                Before you join <span className="ev-gold-grad ev-italic">the constellation.</span>
              </h2>
            </div>
          </div>
          <div className="ev-faq-list">
            {FAQ.map((item, index) => (
              <FaqItem
                key={item.q}
                item={item}
                index={index}
                isOpen={openFaq === index}
                onClick={() => setOpenFaq(openFaq === index ? null : index)}
              />
            ))}
          </div>
        </section>

        <section className="ev-section ev-final">
          <div className="ev-reveal">
            <div className="ev-pill ev-final-pill">
              <span className="ev-pill-dot" />
              Reserve your place
            </div>
            <h2 className="ev-display ev-final-title">
              Ready to enter the <span className="ev-gold-grad ev-italic">constellation?</span>
            </h2>
            <p>
              Join the waitlist. Complete the Brain Scan. If the signal is strong, we invite you into the next step.
            </p>
            <div className="ev-cta-row ev-center-row">
              <Link to="/assessment" className="ev-cta">
                <span>Join the waitlist</span>
                <ArrowIcon />
              </Link>
              <Link to="/login" className="ev-cta-ghost">I have an account</Link>
            </div>
          </div>
        </section>

        <footer className="ev-footer">
          <img className="ev-footer-logo" src="/assets/arc-constellation-logo.png" alt="ARC Constellation" />
          <div>{PRODUCT_NAME}. An early-stage accelerator.</div>
          <div>2026 MYARC.</div>
        </footer>
      </main>
    </div>
  );
}
